import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/components/tooltip';
import { DepositControls, type DepositDraft } from './DepositFields';
import type { BookingReminderInput, Pass } from './passes';

/** An info icon that reveals the given description on hover/focus. */
function OptionInfo({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={text}
            className="text-muted-foreground hover:text-foreground"
          >
            <Info className="size-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * The additional-options controls shared by the create and edit pass forms (PRD #164): "hide from
 * online sale" (in-venue only), a booking reminder (a toggle plus how many days before the booking
 * it is sent), and "confirmation required" (a booking must be confirmed before it is active). The
 * days-before count rides as a string in the draft and is coerced to a number on submit; when the
 * reminder is off it is not sent.
 */
export interface AdditionalOptionsDraft {
  hideFromOnlineSale: boolean;
  bookingReminderEnabled: boolean;
  bookingReminderDaysBefore: string;
  confirmationRequired: boolean;
}

export const emptyAdditionalOptions: AdditionalOptionsDraft = {
  hideFromOnlineSale: false,
  bookingReminderEnabled: false,
  bookingReminderDaysBefore: '',
  confirmationRequired: false,
};

/** Seed the form draft from a persisted pass (edit). */
export function additionalOptionsDraftFrom(pass: Pass): AdditionalOptionsDraft {
  return {
    hideFromOnlineSale: pass.hideFromOnlineSale,
    bookingReminderEnabled: pass.bookingReminder.enabled,
    bookingReminderDaysBefore:
      pass.bookingReminder.daysBefore != null ? String(pass.bookingReminder.daysBefore) : '',
    confirmationRequired: pass.confirmationRequired,
  };
}

export interface AdditionalOptionsPayload {
  hideFromOnlineSale: boolean;
  bookingReminder: BookingReminderInput;
  confirmationRequired: boolean;
}

/** Map the draft back to the three wire fields; the days-before count is omitted when disabled. */
export function additionalOptionsPayload(draft: AdditionalOptionsDraft): AdditionalOptionsPayload {
  const days = Number(draft.bookingReminderDaysBefore);
  return {
    hideFromOnlineSale: draft.hideFromOnlineSale,
    bookingReminder: draft.bookingReminderEnabled
      ? { enabled: true, daysBefore: Number.isFinite(days) && days >= 1 ? days : undefined }
      : { enabled: false },
    confirmationRequired: draft.confirmationRequired,
  };
}

interface AdditionalOptionsFieldsProps {
  draft: AdditionalOptionsDraft;
  onChange: (patch: Partial<AdditionalOptionsDraft>) => void;
  depositDraft: DepositDraft;
  currency: string;
  onDepositChange: (patch: Partial<DepositDraft>) => void;
}

export function AdditionalOptionsFields({
  draft,
  onChange,
  depositDraft,
  currency,
  onDepositChange,
}: AdditionalOptionsFieldsProps) {
  const { t } = useTranslation('common');

  return (
    <fieldset className="grid gap-3 rounded-md border p-4">
      <legend className="px-1 text-sm font-medium">{t('products.form.options.legend')}</legend>

      <DepositControls draft={depositDraft} currency={currency} onChange={onDepositChange} />

      <div className="flex items-center gap-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.hideFromOnlineSale}
            onChange={(event) => onChange({ hideFromOnlineSale: event.target.checked })}
          />
          {t('products.form.options.hideFromOnlineSale')}
        </label>
        <OptionInfo text={t('products.form.options.hideFromOnlineSaleHint')} />
      </div>

      <div className="flex items-center gap-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.bookingReminderEnabled}
            onChange={(event) => onChange({ bookingReminderEnabled: event.target.checked })}
          />
          {t('products.form.options.bookingReminder')}
        </label>
        <OptionInfo text={t('products.form.options.bookingReminderHint')} />
      </div>
      {draft.bookingReminderEnabled && (
        <div className="grid max-w-xs gap-1 pl-6">
          <Label htmlFor="booking-reminder-days">
            {t('products.form.options.bookingReminderDays')}
          </Label>
          <Input
            id="booking-reminder-days"
            type="number"
            min={1}
            value={draft.bookingReminderDaysBefore}
            onChange={(event) => onChange({ bookingReminderDaysBefore: event.target.value })}
            className="w-24"
            required
          />
        </div>
      )}

      <div className="flex items-center gap-1.5">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={draft.confirmationRequired}
            onChange={(event) => onChange({ confirmationRequired: event.target.checked })}
          />
          {t('products.form.options.confirmationRequired')}
        </label>
        <OptionInfo text={t('products.form.options.confirmationRequiredHint')} />
      </div>
    </fieldset>
  );
}
